import React from 'react';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import SearchIcon from '@material-ui/icons/Search';

import Config from '../../constants/appConfig';

const jobDate = (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '-';
const normalizeSearch = (value) => String(value || '').normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const jobKey = (job) => String(job.id_job_link || `${job.id_job}-${job.guid}`);
const jobId = (job) => job && (job.id_job != null ? job.id_job : job.id);
const customPlaneId = 'custom';
const planeConfigurationFields = [
  { id: 'application_speed', label: 'Velocidade de aplicacao', unit: 'km/h' },
  { id: 'travel_speed', label: 'Velocidade de translado', unit: 'km/h' },
  { id: 'balloon_speed', label: 'Velocidade do balao', unit: 'km/h' },
  { id: 'hopper_load', label: 'Hopper', unit: 'L' },
  { id: 'swath_width', label: 'Largura da faixa', unit: 'm' },
  { id: 'balloon_width', label: 'Largura do balao', unit: 'm' },
  { id: 'balloon_length', label: 'Comprimento do balao', unit: 'm' }
];

const defaultPlaneConfiguration = {
  application_speed: 200,
  travel_speed: 200,
  balloon_speed: 200,
  hopper_load: 1600,
  swath_width: 20,
  balloon_width: 400,
  balloon_length: 600
};

const planeConfigurationFrom = (plane, includeDefaults = false) => planeConfigurationFields.reduce((configuration, field) => {
  configuration[field.id] = plane && plane[field.id] != null
    ? plane[field.id]
    : (includeDefaults ? defaultPlaneConfiguration[field.id] : '');
  return configuration;
}, {});

const pointCoordinates = (point) => {
  if (Array.isArray(point) && point.length >= 2) {
    const lat = Number(point[0]);
    const lng = Number(point[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
  }
  if (point && typeof point === 'object') {
    const lat = Number(point.lat != null ? point.lat : point.latitude);
    const lng = Number(point.lng != null ? point.lng : point.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
  }
  return null;
};

const areaRings = (rawPoints) => {
  let points = rawPoints;
  if (typeof points === 'string') {
    try {
      points = JSON.parse(points);
    } catch (error) {
      return [];
    }
  }

  const rings = [];
  const visit = (value) => {
    if (!Array.isArray(value) || !value.length) return;
    const ring = value.map(pointCoordinates).filter(Boolean);
    if (ring.length >= 3) {
      rings.push(ring);
      return;
    }
    value.forEach(visit);
  };
  visit(points);
  return rings;
};

const areaSvgPath = (rawPoints) => {
  const rings = areaRings(rawPoints);
  if (!rings.length) return '';

  const width = 64;
  const height = 42;
  const padding = 4;
  const earthRadius = 6378137;
  const projectedRings = rings.map((ring) => ring.map(([lat, lng]) => {
    const safeLat = Math.max(-85.05112878, Math.min(85.05112878, lat));
    return [
      lng * Math.PI / 180 * earthRadius,
      Math.log(Math.tan((90 + safeLat) * Math.PI / 360)) * earthRadius
    ];
  }));
  const projectedPoints = projectedRings.reduce((all, ring) => all.concat(ring), []);
  const bounds = projectedPoints.reduce((result, point) => ({
    minX: Math.min(result.minX, point[0]),
    maxX: Math.max(result.maxX, point[0]),
    minY: Math.min(result.minY, point[1]),
    maxY: Math.max(result.maxY, point[1])
  }), {
    minX: projectedPoints[0][0], maxX: projectedPoints[0][0],
    minY: projectedPoints[0][1], maxY: projectedPoints[0][1]
  });
  const { minX, maxX, minY, maxY } = bounds;
  const xRange = Math.max(maxX - minX, 1);
  const yRange = Math.max(maxY - minY, 1);
  const scale = Math.min((width - padding * 2) / xRange, (height - padding * 2) / yRange);
  const offsetX = (width - xRange * scale) / 2;
  const offsetY = (height - yRange * scale) / 2;

  return projectedRings.map((ring) => ring.map((point, index) => {
    const x = offsetX + (point[0] - minX) * scale;
    const y = height - offsetY - (point[1] - minY) * scale;
    return `${index ? 'L' : 'M'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ') + ' Z').join(' ');
};

class ImportJobsDialog extends React.Component {
  fileInput = React.createRef();

  areaRequestSequence = 0;

  jobListNode = null;

  jobAreaObserver = null;

  jobItemNodes = new Map();

  jobAreaRequests = new Map();

  state = {
    files: [], titles: [], selectedTitle: '', companies: [], sourceCompany: '', jobs: [], selectedJobs: [],
    jobAreas: {}, activeAreaByJob: {},
    configurationSource: 'file', planes: [], selectedPlaneId: '', planeConfiguration: {}, planesLoading: false,
    titlesLoading: false, companiesLoading: false, jobsLoading: false, importing: false,
    dragActive: false, error: '', success: '', jobSearch: '',
    companySearch: '', companyMenuOpen: false
  };

  componentDidUpdate(prevProps) {
    if (prevProps.open && !this.props.open) this.areaRequestSequence += 1;
    if (!prevProps.open && this.props.open) {
      this.areaRequestSequence += 1;
      const initialFiles = this.props.initialFiles;
      this.setState({
        files: [], titles: [], selectedTitle: '', sourceCompany: '', jobs: [], selectedJobs: [], jobAreas: {}, activeAreaByJob: {}, jobSearch: '',
        companySearch: '', companyMenuOpen: false,
        importing: false, dragActive: false, error: '', success: ''
      }, () => {
        if (initialFiles && initialFiles.length) this.addFiles(initialFiles);
      });
      this.loadTitles();
      this.loadCompanies();
    } else if (this.props.open && prevProps.initialFiles !== this.props.initialFiles
      && this.props.initialFiles && this.props.initialFiles.length) {
      this.addFiles(this.props.initialFiles);
    }
  }

  componentWillUnmount() {
    this.areaRequestSequence += 1;
    this.disconnectJobAreaObserver();
  }

  authHeaders = () => ({ Authorization: localStorage.getItem('access-token-jwt') });

  readResponse = (response) => {
    if (!response.ok) throw response;
    return response.json();
  };

  handleRequestError = (error, message) => {
    if (error && (error.status === 401 || error.status === 403)) {
      this.props.link('/login');
      return;
    }
    this.setState({ error: message });
  };

  loadTitles = () => {
    this.setState({ titlesLoading: true, error: '' });
    fetch(`${Config.server}/job_view_title`, { headers: this.authHeaders() })
      .then(this.readResponse)
      .then((titles) => {
        const availableTitles = Array.isArray(titles) ? titles : [];
        const selectedFromFilter = availableTitles.find((item) => (
          String(item.id) === String(this.props.selectedTitle)
        ));
        this.setState({
          titles: availableTitles,
          selectedTitle: selectedFromFilter
            ? selectedFromFilter.id
            : (availableTitles.length === 1 ? availableTitles[0].id : ''),
          titlesLoading: false
        });
      })
      .catch((error) => {
        this.setState({ titlesLoading: false });
        this.handleRequestError(error, 'Nao foi possivel carregar os grupos disponiveis.');
      });
  };

  loadCompanies = () => {
    this.setState({ companiesLoading: true, error: '' });
    fetch(`${Config.server}/job_view/import_companies/all`, { headers: this.authHeaders() })
      .then(this.readResponse)
      .then((companies) => this.setState({
        companies: Array.isArray(companies) ? companies : [], companiesLoading: false
      }))
      .catch((error) => {
        this.setState({ companiesLoading: false });
        this.handleRequestError(error, 'Nao foi possivel carregar as empresas disponiveis.');
      });
  };

  loadPlanes = () => {
    this.setState({ planesLoading: true, error: '' });
    fetch(`${Config.server}/plane`, { headers: this.authHeaders() })
      .then(this.readResponse)
      .then((planes) => this.setState((state) => {
        const availablePlanes = Array.isArray(planes) ? planes : [];
        const selectedPlaneId = state.selectedPlaneId === customPlaneId
          || availablePlanes.some((plane) => String(plane.id) === String(state.selectedPlaneId))
          ? state.selectedPlaneId
          : customPlaneId;
        return { planes: availablePlanes, selectedPlaneId, planesLoading: false };
      }))
      .catch((error) => {
        this.setState({ planesLoading: false });
        this.handleRequestError(error, 'Nao foi possivel carregar as aeronaves cadastradas.');
      });
  };

  changeConfigurationSource = (event) => {
    const configurationSource = event.target.value;
    if (configurationSource === 'plane') {
      this.setState({
        configurationSource,
        selectedPlaneId: customPlaneId,
        planeConfiguration: planeConfigurationFrom(null, true),
        error: ''
      }, this.loadPlanes);
      return;
    }
    this.setState({ configurationSource, error: '' });
  };

  selectPlane = (event) => {
    const selectedPlaneId = event.target.value;
    if (selectedPlaneId === customPlaneId) {
      this.setState({
        selectedPlaneId,
        planeConfiguration: planeConfigurationFrom(null, true),
        error: ''
      });
      return;
    }
    const plane = this.state.planes.find((item) => String(item.id) === String(selectedPlaneId));
    this.setState({
      selectedPlaneId,
      planeConfiguration: plane ? planeConfigurationFrom(plane) : {},
      error: ''
    });
  };

  changePlaneConfiguration = (field) => (event) => {
    const value = event.target.value;
    this.setState((state) => ({
      planeConfiguration: { ...state.planeConfiguration, [field]: value }
    }));
  };

  selectCompany = (event, company) => {
    const sourceCompany = company ? company.id : '';
    const requestSequence = ++this.areaRequestSequence;
    this.setState({
      sourceCompany, jobs: [], selectedJobs: [], jobAreas: {}, activeAreaByJob: {}, jobSearch: '',
      companySearch: company ? company.name : '', companyMenuOpen: false,
      jobsLoading: Boolean(sourceCompany), error: ''
    });
    if (!sourceCompany) return;

    fetch(`${Config.server}/job_view/import_jobs/${sourceCompany}`, { headers: this.authHeaders() })
      .then(this.readResponse)
      .then((jobs) => {
        if (requestSequence !== this.areaRequestSequence) return;
        const filteredJobs = Array.isArray(jobs) ? jobs.reduce((accumulator, current) => {
          const isDuplicate = accumulator.some(item => item.id_job === current.id_job);
          if (!isDuplicate) {
            accumulator.push(current);
          }
          return accumulator;
        }, []) : [];

        this.setState({ jobs: filteredJobs, jobsLoading: false }, this.observeJobItems);
      })
      .catch((error) => {
        if (requestSequence !== this.areaRequestSequence) return;
        this.setState({ jobsLoading: false });
        this.handleRequestError(error, 'Nao foi possivel carregar os trabalhos da empresa.');
      });
  };

  disconnectJobAreaObserver = () => {
    if (this.jobAreaObserver) this.jobAreaObserver.disconnect();
    if (this.jobListNode) this.jobListNode.removeEventListener('scroll', this.loadVisibleJobAreas);
    this.jobAreaObserver = null;
  };

  setJobListRef = (node) => {
    if (this.jobListNode === node) return;
    this.disconnectJobAreaObserver();
    this.jobListNode = node;
    if (!node) return;

    if (typeof window !== 'undefined' && window.IntersectionObserver) {
      this.jobAreaObserver = new window.IntersectionObserver(this.handleJobAreaVisibility, {
        root: node,
        threshold: 0.01
      });
    } else {
      node.addEventListener('scroll', this.loadVisibleJobAreas);
    }
    this.observeJobItems();
  };

  setJobItemRef = (job, node) => {
    const key = jobKey(job);
    const previous = this.jobItemNodes.get(key);
    if (previous && this.jobAreaObserver) this.jobAreaObserver.unobserve(previous.node);
    if (!node) {
      this.jobItemNodes.delete(key);
      return;
    }

    const item = { job, node };
    this.jobItemNodes.set(key, item);
    if (this.jobAreaObserver) this.jobAreaObserver.observe(node);
    else this.loadVisibleJobAreas();
  };

  observeJobItems = () => {
    if (this.jobAreaObserver) {
      this.jobItemNodes.forEach((item) => this.jobAreaObserver.observe(item.node));
    }
    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(this.loadVisibleJobAreas);
    } else this.loadVisibleJobAreas();
  };

  handleJobAreaVisibility = (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const item = Array.from(this.jobItemNodes.values()).find((current) => current.node === entry.target);
      if (item) this.loadJobArea(item.job);
    });
  };

  loadVisibleJobAreas = () => {
    if (!this.jobListNode) return;
    const listBounds = this.jobListNode.getBoundingClientRect();
    this.jobItemNodes.forEach((item) => {
      const bounds = item.node.getBoundingClientRect();
      if (bounds.bottom > listBounds.top && bounds.top < listBounds.bottom) this.loadJobArea(item.job);
    });
  };

  loadJobArea = (job) => {
    if (!this.props.open) return;
    const key = jobKey(job);
    const requestSequence = this.areaRequestSequence;
    if (this.state.jobAreas[key] || this.jobAreaRequests.get(key) === requestSequence) return;

    const id = jobId(job);
    if (id == null) {
      this.setState((state) => ({
        jobAreas: { ...state.jobAreas, [key]: { loading: false, failed: true, items: [] } }
      }));
      return;
    }

    this.jobAreaRequests.set(key, requestSequence);
    this.setState((state) => ({
      jobAreas: { ...state.jobAreas, [key]: { loading: true, failed: false, items: [] } }
    }));
    fetch(`${Config.server}/job_area/${encodeURIComponent(id)}/execucao`, {
      method: 'GET', headers: this.authHeaders()
    })
      .then(this.readResponse)
      .then((areas) => {
        if (requestSequence !== this.areaRequestSequence) return;
        const items = Array.isArray(areas) ? areas.map((area) => ({
          ...area,
          svgPath: areaSvgPath(area.points)
        })) : [];
        this.setState((state) => ({
          jobAreas: { ...state.jobAreas, [key]: { loading: false, failed: false, items } }
        }));
      })
      .catch((error) => {
        if (requestSequence !== this.areaRequestSequence) return;
        this.setState((state) => ({
          jobAreas: { ...state.jobAreas, [key]: { loading: false, failed: true, items: [] } }
        }));
        if ((error.status === 401 || error.status === 403) && typeof this.props.link === 'function') {
          this.props.link('/login');
        }
      })
      .then(() => {
        if (this.jobAreaRequests.get(key) === requestSequence) this.jobAreaRequests.delete(key);
      });
  };

  addFiles = (fileList) => {
    const received = Array.from(fileList || []);
    const supported = received.filter((file) => /\.(zjob|zip|log)$/i.test(file.name));
    const rejected = received.length - supported.length;
    this.setState((state) => {
      const files = state.files.slice();
      supported.forEach((file) => {
        const exists = files.some((item) => item.name === file.name
          && item.size === file.size && item.lastModified === file.lastModified);
        if (!exists) files.push(file);
      });
      return {
        files,
        error: rejected ? 'Use arquivos ZJOB, ZIP do AGNAV ou LOG do SATLOC.' : '',
        success: ''
      };
    });
  };

  chooseFiles = (event) => {
    this.addFiles(event.target.files);
    event.target.value = null;
  };

  dropFiles = (event) => {
    event.preventDefault();
    this.setState({ dragActive: false });
    this.addFiles(event.dataTransfer.files);
  };

  toggleJob = (job) => {
    const key = jobKey(job);
    this.setState((state) => ({
      selectedJobs: state.selectedJobs.includes(key)
        ? state.selectedJobs.filter((item) => item !== key)
        : state.selectedJobs.concat(key),
      success: ''
    }));
  };

  getVisibleJobs = () => {
    const { jobs, selectedJobs, jobSearch } = this.state;
    const search = normalizeSearch(jobSearch);
    return jobs.filter((job) => {
      if (selectedJobs.includes(jobKey(job)) || !search) return true;
      return normalizeSearch([
        job.title, job.id_job, job.plane_name, job.client_name, job.type,
        jobDate(job.datetime || job.created)
      ].join(' ')).includes(search);
    }).sort((first, second) => {
      const firstSelected = selectedJobs.includes(jobKey(first));
      const secondSelected = selectedJobs.includes(jobKey(second));
      if (firstSelected !== secondSelected) return firstSelected ? -1 : 1;
      return 0;
    });
  };

  getVisibleCompanies = () => {
    const search = normalizeSearch(this.state.companySearch);
    if (!search) return this.state.companies;
    return this.state.companies.filter((company) => normalizeSearch(company.name).includes(search));
  };

  changeCompanySearch = (event) => {
    const companySearch = event.target.value;
    this.areaRequestSequence += 1;
    this.setState({
      companySearch,
      companyMenuOpen: true,
      sourceCompany: '',
      jobs: [],
      selectedJobs: [],
      jobAreas: {},
      activeAreaByJob: {},
      jobSearch: ''
    });
  };

  setActiveJobArea = (job, index) => {
    const key = jobKey(job);
    if (this.state.activeAreaByJob[key] === index) return;
    this.setState((state) => ({
      activeAreaByJob: { ...state.activeAreaByJob, [key]: index }
    }));
  };

  renderJobAreas = (job) => {
    const key = jobKey(job);
    const areaState = this.state.jobAreas[key];
    if (!areaState) return null;
    if (areaState.loading) {
      return <div className="job-yield-job-areas-loading" title="Carregando areas">
        <CircularProgress size={16} />
      </div>;
    }
    if (areaState.failed) {
      return <div className="job-yield-job-areas-unavailable">Areas indisponiveis</div>;
    }
    if (!areaState.items.length) return null;

    const stackShift = Math.min(56, Math.max(0, (areaState.items.length - 1) * 9));
    const offset = areaState.items.length > 1 ? stackShift / (areaState.items.length - 1) : 0;
    const selectedIndex = this.state.activeAreaByJob[key];
    const activeIndex = selectedIndex >= 0 && selectedIndex < areaState.items.length ? selectedIndex : 0;

    return <div className="job-yield-job-areas" style={{ width: `calc(8rem + ${stackShift}px)` }}>
      {areaState.items.map((area, index) => {
        const excluded = area.type === 'X' || area.x;
        const isActive = index === activeIndex;
        return <div
          className={`job-yield-job-area-card ${excluded ? 'excluded' : ''} ${isActive ? 'active' : ''}`}
          key={area.id || area.guid || index}
          title={area.name || `Area ${index + 1}`}
          style={{
            left: `${index * offset}px`,
            zIndex: isActive ? areaState.items.length + 1 : areaState.items.length - Math.abs(activeIndex - index)
          }}
          onMouseEnter={() => this.setActiveJobArea(job, index)}
        >
          <svg viewBox="0 0 64 42" role="img" aria-label={area.name || `Area ${index + 1}`}>
            {area.svgPath && <path d={area.svgPath} fillRule="evenodd" />}
          </svg>
          <span>{area.name || `Area ${index + 1}`}</span>
        </div>;
      })}
    </div>;
  };

  uploadFile = (file, idTitle, configurationSource = this.state.configurationSource, planeId = this.state.selectedPlaneId, planeConfiguration = this.state.planeConfiguration) => {
    const formData = new FormData();
    const customConfiguration = configurationSource === 'plane' && planeId === customPlaneId;
    const selectedConfigurationSource = customConfiguration ? 'custom' : configurationSource;
    formData.append('file', file, file.name);
    formData.append('id_title', idTitle);
    formData.append('configuration_source', selectedConfigurationSource);
    if (selectedConfigurationSource === 'plane') {
      formData.append('plane_id', planeId);
    }
    if (selectedConfigurationSource === 'plane' || selectedConfigurationSource === 'custom') {
      formData.append('plane_configuration', JSON.stringify(planeConfiguration));
    }
    return fetch(`${Config.server}/job_view`, {
      method: 'POST', body: formData, headers: this.authHeaders()
    }).then(this.readResponse);
  };

  importJobs = () => {
    const {
      files, selectedTitle, sourceCompany, selectedJobs, jobs,
      configurationSource, selectedPlaneId, planeConfiguration
    } = this.state;
    const selectedConfigurationSource = configurationSource === 'plane' && selectedPlaneId === customPlaneId
      ? 'custom'
      : configurationSource;
    const startedAt = Date.now();
    const pendingItems = files.map((file, index) => ({
      key: `file-${startedAt}-${index}`,
      id_title: selectedTitle,
      title: file.name.replace(/\.(zjob|zip|log)$/i, '') || file.name,
      created: startedAt
    })).concat(selectedJobs.map((key, index) => {
      const job = jobs.find((item) => jobKey(item) === key);
      return {
        key: `system-${startedAt}-${index}`,
        id_title: selectedTitle,
        title: job && job.title ? job.title : `Trabalho importado ${index + 1}`,
        created: startedAt
      };
    }));
    const requests = files.map((file) => this.uploadFile(
      file, selectedTitle, configurationSource, selectedPlaneId, planeConfiguration
    ));
    if (sourceCompany && selectedJobs.length) {
      const selectedItems = selectedJobs.map((key) => {
        const job = jobs.find((item) => jobKey(item) === key);
        return job ? { id_job_link: job.id_job_link, guid: job.guid } : null;
      }).filter(Boolean);
      requests.push(fetch(`${Config.server}/job_view`, {
        method: 'PUT',
        headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_company: sourceCompany,
          id_title: selectedTitle,
          list: selectedItems,
          configuration_source: selectedConfigurationSource,
          plane_id: selectedConfigurationSource === 'plane' ? selectedPlaneId : undefined,
          plane_configuration: selectedConfigurationSource === 'plane' || selectedConfigurationSource === 'custom'
            ? planeConfiguration
            : undefined
        })
      }).then(this.readResponse));
    }
    if (!requests.length) return;

    this.setState({ importing: true, error: '', success: '' });
    Promise.all(requests)
      .then((responses) => {
        let startedTasks = responses.slice(0, files.length);
        if (sourceCompany && selectedJobs.length) {
          const systemResponse = responses[files.length];
          startedTasks = startedTasks.concat(
            systemResponse && Array.isArray(systemResponse.tasks) ? systemResponse.tasks : []
          );
        }
        const trackedItems = pendingItems.map((item, index) => {
          const task = startedTasks[index] || {};
          return {
            ...item,
            id_task: task.id_task || null,
            title: task.title || item.title,
            progress: Number(task.progress) || 0,
            status: task.id_task ? (task.status || 'processing') : 'canceled'
          };
        });

        this.setState({
          importing: false, files: [], selectedJobs: [], success: ''
        });
        if (this.props.onImported) this.props.onImported(trackedItems, selectedTitle);
        if (this.props.onClose) this.props.onClose();
      })
      .catch((error) => {
        this.setState({ importing: false });
        this.handleRequestError(error, 'Nao foi possivel iniciar a importacao. Verifique os arquivos selecionados.');
      });
  };

  render() {
    const { open, onClose } = this.props;
    const {
      files, titles, selectedTitle, sourceCompany, selectedJobs, titlesLoading, companiesLoading,
      jobsLoading, importing, dragActive, error, success, jobSearch,
      companySearch, companyMenuOpen, configurationSource, planes, selectedPlaneId,
      planeConfiguration, planesLoading
    } = this.state;
    const selectedPlane = planes.find((plane) => String(plane.id) === String(selectedPlaneId));
    const canImport = Boolean(selectedTitle)
      && (files.length > 0 || selectedJobs.length > 0)
      && (configurationSource === 'file' || Boolean(selectedPlane) || selectedPlaneId === customPlaneId);
    const visibleJobs = this.getVisibleJobs();
    const visibleCompanies = this.getVisibleCompanies();

    return <Dialog
      open={open} onClose={importing ? undefined : onClose} maxWidth="md" fullWidth
      PaperProps={{ className: 'job-yield-import-dialog' }}
    >
      <DialogTitle className="job-yield-import-title" disableTypography>
        <div><CloudUploadOutlinedIcon /></div>
        <div><strong>Importar novos trabalhos</strong><span>Envie arquivos ou selecione trabalhos existentes no sistema</span></div>
      </DialogTitle>
      <DialogContent className="job-yield-import-content">
        <section className="job-yield-import-section">
          <div className="job-yield-import-section-title">
            <DescriptionOutlinedIcon />
            <div><strong>Grupo da visualizacao</strong><span>Escolha em qual grupo os trabalhos serao organizados</span></div>
          </div>
          <TextField
            select variant="outlined" size="small" fullWidth label="Grupo"
            value={selectedTitle}
            disabled={titlesLoading || importing || !titles.length}
            onChange={(event) => this.setState({ selectedTitle: event.target.value, error: '' })}
            InputProps={{
              endAdornment: titlesLoading ? <CircularProgress size={18} /> : null
            }}
          >
            {titles.map((title) => <MenuItem key={title.id} value={title.id}>{title.title}</MenuItem>)}
          </TextField>
          {!titlesLoading && !titles.length && <div className="job-yield-import-empty">
            Nenhum grupo de visualizacao foi cadastrado para esta empresa.
          </div>}
        </section>

        <div className="job-yield-import-or"></div>

        <div className="job-yield-import-local-grid">
          <div className="job-yield-import-local-column">
          <section className="job-yield-import-section job-yield-local-file-section">
            <div className="job-yield-import-section-title">
              <CloudUploadOutlinedIcon />
              <div><strong>Arquivo local</strong><span>Escolha em uma pasta ou arraste para a area abaixo</span></div>
            </div>
            <input ref={this.fileInput} hidden multiple type="file" accept=".zjob,.zip,.log" onChange={this.chooseFiles} />
            <div
              className={`job-yield-dropzone ${dragActive ? 'active' : ''}`}
              onClick={() => this.fileInput.current && this.fileInput.current.click()}
              onDragEnter={(event) => { event.preventDefault(); this.setState({ dragActive: true }); }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={(event) => {
                event.preventDefault();
                if (!event.currentTarget.contains(event.relatedTarget)) this.setState({ dragActive: false });
              }}
              onDrop={this.dropFiles}
            >
              <CloudUploadOutlinedIcon />
              <strong>Arraste arquivos ZJOB, ZIP (AGNAV) ou LOG (SATLOC) aqui</strong>
              <span>ou clique para selecionar em uma pasta</span>
            </div>
            {files.length > 0 && <div className="job-yield-selected-files">
              {files.map((file) => <div key={`${file.name}-${file.lastModified}`}>
                <DescriptionOutlinedIcon /><span>{file.name}</span>
                <button type="button" onClick={() => this.setState({ files: files.filter((item) => item !== file) })}>Remover</button>
              </div>)}
            </div>}
          </section>

        <section className="job-yield-import-section job-yield-system-section">
          <div className="job-yield-import-section-title">
              <BusinessOutlinedIcon />
              <div><strong>Trabalho existente no sistema</strong><span>Selecione a empresa e os trabalhos que deseja processar</span></div>
            </div>
            <div className="job-yield-company-picker">
              <TextField
                variant="outlined" size="small" fullWidth label="Empresa"
                placeholder="Digite para buscar uma empresa"
                value={companySearch}
                disabled={companiesLoading || importing}
                onFocus={() => this.setState({ companyMenuOpen: true })}
                onBlur={() => window.setTimeout(() => this.setState({ companyMenuOpen: false }), 120)}
                onChange={this.changeCompanySearch}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && visibleCompanies.length) {
                    event.preventDefault();
                    this.selectCompany(event, visibleCompanies[0]);
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  endAdornment: companiesLoading ? <CircularProgress size={18} /> : null
                }}
              />
              {companyMenuOpen && !companiesLoading && <div className="job-yield-company-options" role="listbox">
                {visibleCompanies.map((company) => <button
                  type="button" role="option" key={company.id}
                  aria-selected={String(company.id) === String(sourceCompany)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => this.selectCompany(event, company)}
                >{company.name}</button>)}
                {!visibleCompanies.length && <div>Nenhuma empresa encontrada</div>}
              </div>}
            </div>
            {companiesLoading && <div className="job-yield-import-loading"><CircularProgress size={24} /></div>}
            {sourceCompany && !companiesLoading && <React.Fragment>
              <TextField
                className="job-yield-job-search"
                variant="outlined" size="small" fullWidth
                value={jobSearch}
                onChange={(event) => this.setState({ jobSearch: event.target.value })}
                placeholder="Digite para buscar por trabalho, aeronave, cliente ou data"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                }}
              />
              <div className="job-yield-system-jobs" ref={this.setJobListRef}>
              {jobsLoading && <div className="job-yield-import-loading"><CircularProgress size={24} /></div>}
              {!jobsLoading && visibleJobs.map((job) => <label
                key={jobKey(job)}
                ref={(node) => this.setJobItemRef(job, node)}
                className={`job-yield-system-job ${selectedJobs.includes(jobKey(job)) ? 'selected' : ''}`}
              >
                <Checkbox
                  color="primary" checked={selectedJobs.includes(jobKey(job))}
                  onChange={() => this.toggleJob(job)}
                />
                <div className="job-yield-system-job-info"><strong>{job.title || `Trabalho #${job.id_job}`}</strong>
                  <span>{jobDate(job.datetime || job.created)} · {job.plane_name || 'Aeronave nao informada'} · {job.type}</span>
                </div>
                {this.renderJobAreas(job)}
              </label>)}
              {!jobsLoading && !visibleJobs.length && <div className="job-yield-import-empty">Nenhum trabalho encontrado para esta busca.</div>}
              </div>
            </React.Fragment>}
        </section>
          </div>
          <section className="job-yield-import-section job-yield-planning-config">
            <div className="job-yield-import-section-title">
              <DescriptionOutlinedIcon />
              <div><strong>Configuracao do planejamento</strong><span>Defina fonte e valores usados ao gerar planejamento</span></div>
            </div>
            <TextField
              select variant="outlined" size="small" fullWidth label="Usar configuracoes do"
              value={configurationSource} disabled={importing}
              onChange={this.changeConfigurationSource}
            >
              <MenuItem value="file">Arquivo importado</MenuItem>
              <MenuItem value="plane">Aeronave cadastrada</MenuItem>
            </TextField>
            {configurationSource === 'plane' && <React.Fragment>
              <TextField
                select variant="outlined" size="small" fullWidth required
                className="job-yield-plane-picker" label="Aeronave"
                value={selectedPlaneId} disabled={planesLoading || importing}
                onChange={this.selectPlane}
                InputProps={{ endAdornment: planesLoading ? <CircularProgress size={18} /> : null }}
              >
                <MenuItem value={customPlaneId}>Customizado</MenuItem>
                {!planes.length && <MenuItem value="" disabled>Nenhuma aeronave cadastrada</MenuItem>}
                {planes.map((plane) => <MenuItem key={plane.id} value={plane.id}>
                  {plane.name || `Aeronave #${plane.id}`}{plane.serial ? ` · ${plane.serial}` : ''}
                </MenuItem>)}
              </TextField>
              {!planesLoading && !planes.length && <div className="job-yield-import-empty">
                Nenhuma aeronave cadastrada para esta empresa.
              </div>}
              <div className="job-yield-plane-config" aria-label="Configuracoes personalizadas do planejamento">
                <div className="job-yield-plane-config-title">
                  <strong>Valores personalizados</strong>
                  <span>{selectedPlane ? 'Iniciados com os valores da aeronave selecionada' : 'Iniciados com os valores padrao do planejamento'}</span>
                </div>
                {planeConfigurationFields.map((field) => <TextField
                  key={field.id} variant="outlined" size="small" fullWidth
                  className="job-yield-plane-config-field" label={field.label}
                  value={planeConfiguration[field.id] == null ? '' : planeConfiguration[field.id]}
                  disabled={importing} inputProps={{ inputMode: 'decimal' }}
                  onChange={this.changePlaneConfiguration(field.id)}
                  InputProps={{ endAdornment: <InputAdornment position="end">{field.unit}</InputAdornment> }}
                />)}
              </div>
            </React.Fragment>}
          </section>

        </div>
        {error && <div className="job-yield-import-message error">{error}</div>}
        {success && <div className="job-yield-import-message success">{success}</div>}
      </DialogContent>
      <DialogActions className="job-yield-import-actions">
        <Button className="job-yield-import-close" onClick={onClose} disabled={importing}>Cancelar</Button>
        <Button
          variant="contained" className="job-yield-import-submit"
          startIcon={importing ? <CircularProgress size={18} /> : <CloudUploadOutlinedIcon />}
          disabled={!canImport || importing} onClick={this.importJobs}
        >{importing ? 'Iniciando...' : 'Gerar trabalhos'}</Button>
      </DialogActions>
    </Dialog>;
  }
}

export default ImportJobsDialog;
